import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ViewerHost } from "../ViewerHost";
import { EditorShell } from "../../ui/editor/EditorShell";
import type { ModelLoadStatus } from "../../ui/editor/ModelLoadOverlay";
import ConfiguratorBuilder from "./ConfiguratorBuilder";
import type { ViewerClient } from "../../client/ViewerClient";
import { getModel, getModelGlbUrl, saveModelConfig, saveModelPoster } from "../../features/vault/vaultApi";
import { lostFilesSentence, savedSceneLostBackdrop, savedSceneReport } from "../../util/savedScene";
import { capsFor } from "../../ui/editor/caps";
import { captureHostFor } from "../../ui/editor/scene/sceneFile";
import { withEmbeddedScene } from "../../util/embeddedScene";
import { beginSceneRestore, type SceneRestore } from "../../state/editor/modelLoads";
import { StudioTopBarActions, StudioTopBarLeading } from "./StudioTopBarActions";
import { OPEN_CONFIGURATOR_DETAILS_EVENT } from "../../ui/editor/configurator/draft";
import { useToast } from "../../shared/ui";

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/** The scene a shared editor link carries, or undefined for a dead or expired token. */
async function resolveSharedScene(token: string): Promise<string | undefined> {
  try {
    const res = await fetch(`/api/v1/embeds/${encodeURIComponent(token)}/resolve`);
    const json = await res.json();
    const shared = json?.data?.config?.vjson;
    return typeof shared === "string" && shared ? shared : undefined;
  } catch {
    return undefined; // falls back to the model's saved scene
  }
}

/**
 * Loads a Vault model into the ring editor: fetches a short-TTL signed GLB URL,
 * `addModelUrl`s it, applies any saved vjson config, and (once) captures a
 * poster snapshot for the Drive thumbnail. Renders nothing.
 */
function StudioModelLoader({
  ready,
  client,
  modelId,
  onName,
  onStatus,
}: {
  ready: boolean;
  client: ViewerClient | null;
  modelId: string;
  onName: (name: string) => void;
  /** Drives the editor's load overlay — see ModelLoadStatus. */
  onStatus: (s: ModelLoadStatus) => void;
}) {
  const loadedFor = useRef<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!ready || !client || loadedFor.current === modelId) return;
    loadedFor.current = modelId;
    let alive = true;
    (async () => {
      onStatus({ phase: "resolving" });
      // Open while a saved scene is being restored; the "Apply default
      // materials" dialog stays away from the model it brings in.
      let restore: SceneRestore | null = null;
      try {
        const model = await getModel(modelId);
        if (!alive) return;
        onName(model.name);
        // A shared editor link (?embed=<token>) carries the scene as it looked
        // when the link was made, which is not necessarily the model's last
        // save. Resolve it and let it win — same apply path, same backdrop
        // warning below, so a shared scene cannot skip that guard. Resolved
        // BEFORE the model is added (alongside the signed URL, so it costs no
        // extra round trip): whether this load restores a scene has to be
        // known before the model appears, not after.
        const embedToken = new URLSearchParams(window.location.search).get("embed");
        const [{ url }, shared] = await Promise.all([
          getModelGlbUrl(modelId),
          embedToken ? resolveSharedScene(embedToken) : Promise.resolve(undefined),
        ]);
        if (!alive) return;
        const vjson = shared ?? (model.config as { vjson?: string } | undefined)?.vjson;
        // CAD imports (3dm/obj/fbx/stl) arrive in mm / off-origin — center + fit.
        const native = ["3dm", "obj", "fbx", "stl"].includes(model.format);
        // v65: a "Ring + scene" file carries its own look. A scene saved in
        // the Vault (or a shared link's) is newer than the file's and wins;
        // without one, the file's own look is the scene.
        const v65 = capsFor(client.contractVersion).sceneFileV65;
        const ownScene = v65 && model.embeddedScene === true && !vjson;
        onStatus({ phase: "loading", name: model.name });
        // Synchronously before addModelUrl posts its RPC — see modelLoads.ts
        // for why that ordering is what rules out a flash of the dialog.
        if (vjson || ownScene) restore = beginSceneRestore(client);
        const addedId = await client.ring.addModelUrl(
          url,
          withEmbeddedScene(native ? { autoCenter: true, autoScale: true } : undefined, v65, !!vjson),
        );
        restore?.claim(addedId);
        if (!alive) return;
        if (vjson) {
          onStatus({ phase: "applying", name: model.name });
          try {
            await client.ring.loadVjsonFile(
              new File([vjson], "config.vjson", { type: "application/json" }),
            );
            // The one saved setting that can silently not arrive: a backdrop
            // picked from disk that was too large to store inside the config.
            // The viewer keeps the live background instead of blanking the
            // scene to transparent, so nothing on screen says it is missing.
            if (alive && savedSceneLostBackdrop(vjson)) {
              toast({
                tone: "warning",
                title: "Backdrop not restored",
                description: "This scene was saved with a local image background that was too large to store. The current background was kept.",
              });
            }
          } catch {
            /* config apply best-effort */
          }
        }
        if (!alive) return;
        onStatus({ phase: "done" });

        if (!model.posterKey) {
          setTimeout(async () => {
            // Model was swapped or the editor unmounted before the timer fired —
            // don't snapshot whatever scene is showing now and save it as THIS
            // model's poster (the viewer is shared across model switches).
            if (!alive) return;
            try {
              const blob = await client.ring.snapshot({ width: 512, height: 512 });
              if (!alive) return;
              const dataUrl = await blobToDataURL(blob);
              await saveModelPoster(modelId, dataUrl);
            } catch {
              /* snapshot optional */
            }
          }, 1800);
        }
      } catch (e) {
        // There is no "editor's own error handling" to fall back on: a failed
        // resolve/fetch used to leave the studio sitting on its drag-and-drop
        // empty state forever, indistinguishable from an empty scene. Surface
        // it and let the user retry.
        if (!alive) return;
        loadedFor.current = null; // allow a retry to re-run this effect
        onStatus({
          phase: "error",
          message: e instanceof Error ? e.message : "Could not load this model.",
        });
      } finally {
        restore?.end();
      }
    })();
    return () => {
      alive = false;
    };
  }, [ready, client, modelId, onName, onStatus, toast]);

  return null;
}

/**
 * Why the model-bound studio actions (Save / thumbnail / Configurator) can't run
 * yet, or undefined when they can. The viewer handshake resolves long before the
 * GLB has streamed in, so gating on `ready` alone let the configurator open
 * against an empty scene and report "No metal or gem groups detected".
 */
function configuratorHint(
  ready: boolean,
  modelId: string | undefined,
  modelLoad: ModelLoadStatus,
): string | undefined {
  if (!modelId) return "Open a model from the Vault to build its configurator";
  if (!ready) return "Waiting for the viewer…";
  if (modelLoad.phase === "error") return "This model didn't load — retry it first";
  if (modelLoad.phase !== "done") return "Loading this model…";
  return undefined;
}

/** A file from the user's computer up to this size rides inside a Vault save. */
const SAVE_INLINE_BUDGET = 4 * 1024 * 1024;

export default function StudioPage() {
  const { modelId } = useParams<{ modelId: string }>();
  // The studio had two chromes and a pill to switch them, remembered in
  // localStorage["vault:canvasVersion"]. One chrome now; clear the key so a
  // stale "v2" cannot haunt whatever reuses that name next.
  useEffect(() => {
    try { localStorage.removeItem("vault:canvasVersion"); } catch { /* private mode */ }
  }, []);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [thumbState, setThumbState] = useState<"idle" | "busy" | "done">("idle");
  const [modelLoad, setModelLoad] = useState<ModelLoadStatus>({ phase: "idle" });
  // Bumping this remounts StudioModelLoader, which re-runs its effect from scratch.
  const [loadAttempt, setLoadAttempt] = useState(0);
  const { toast } = useToast();

  // The canvas bar's price tag asks for the drawer by name (it is editor code;
  // the drawer is ours).
  useEffect(() => {
    const open = () => setShowBuilder(true);
    window.addEventListener(OPEN_CONFIGURATOR_DETAILS_EVENT, open);
    return () => window.removeEventListener(OPEN_CONFIGURATOR_DETAILS_EVENT, open);
  }, []);

  const updateThumbnail = async (client: ViewerClient | null) => {
    if (!client || !modelId) return;
    setThumbState("busy");
    try {
      // Snapshot the CURRENT frame (whatever materials/camera are set right now).
      // 1024, matching what the Canvas chrome's Shot button captured. 640 was
      // soft on a retina vault grid.
      const blob = await client.ring.snapshot({ width: 1024, height: 1024 });
      const dataUrl = await blobToDataURL(blob);
      await saveModelPoster(modelId, dataUrl);
      setThumbState("done");
      setTimeout(() => setThumbState("idle"), 1800);
    } catch {
      // Silence here read as "nothing happened". It can also mean the
      // configurator's preview interlock refused to hand over a scene it could
      // not first put back — either way the user needs to know it didn't stick.
      setThumbState("idle");
      toast({ tone: "error", title: "Could not update the thumbnail", description: "Nothing was saved. Try again." });
    }
  };

  const handleSave = async (client: ViewerClient | null) => {
    if (!client || !modelId) return;
    setSaving(true);
    try {
      // v65: the whole scene (of-scene/2). Files are saved by address where
      // they have one; a file from the user's computer is kept inline up to
      // the budget (Mongo documents cap at 16 MB), and one over it is named in
      // a warning below. The configurator is left out: the Vault keeps its own
      // (config.configurator), saved from the configurator itself.
      const v65 = capsFor(client.contractVersion).sceneFileV65;
      const vjson = v65
        ? await client.ring.exportSceneJSON({
            assets: "reference",
            inlineBudgetBytes: SAVE_INLINE_BUDGET,
            host: captureHostFor(client, { configurator: false }),
          })
        : await client.ring.exportVjson({ pretty: false });
      await saveModelConfig(modelId, { vjson });
      setSaved(true);
      setTimeout(() => setSaved(false), 1600);
      const lost = v65 ? lostFilesSentence(savedSceneReport(vjson), SAVE_INLINE_BUDGET / (1024 * 1024)) : null;
      if (lost) toast({ tone: "warning", title: "Some files were not saved", description: lost, duration: 9000 });
    } catch {
      // Same reason as updateThumbnail: a save that quietly does nothing is how
      // people lose work they believe is on disk.
      toast({ tone: "error", title: "Could not save the scene", description: "Nothing was saved. Try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ViewerHost mode="ring">
      {({ ready, client, iframe, viewerOrigin }) => {
        const hint = configuratorHint(ready, modelId, modelLoad);
        return (
        <div className="relative h-screen">
          {modelId && (
            <StudioModelLoader
              key={loadAttempt}
              ready={ready}
              client={client}
              modelId={modelId}
              onName={setName}
              onStatus={setModelLoad}
            />
          )}
          <EditorShell
            iframe={iframe}
            ready={ready}
            viewerOrigin={viewerOrigin}
            modelLoad={modelLoad}
            onRetryModel={() => { setModelLoad({ phase: "idle" }); setLoadAttempt((n) => n + 1); }}
            onFullscreen={() => void document.documentElement.requestFullscreen?.()}
            modelId={modelId}
            modelName={name || undefined}
            topBarLeading={<StudioTopBarLeading modelId={modelId} />}
            overlay={
              // Inside the editor's providers: the drawer draws the material
              // library and shares the canvas bar's draft.
              <AnimatePresence>
                {showBuilder && modelId && !hint && (
                  <ConfiguratorBuilder modelId={modelId} onClose={() => setShowBuilder(false)} />
                )}
              </AnimatePresence>
            }
            topBarActions={
              <StudioTopBarActions
                client={client}
                modelId={modelId}
                hint={hint}
                saving={saving}
                saved={saved}
                thumbState={thumbState}
                onSave={() => handleSave(client)}
                onThumbnail={() => updateThumbnail(client)}
                onOpenConfigurator={() => setShowBuilder((v) => !v)}
              />
            }
          />

        </div>
        );
      }}
    </ViewerHost>
  );
}
